const REASONING_BLOCKS = [
  /<think\b[^>]*>[\s\S]*?<\/think>/gi,
  /<analysis\b[^>]*>[\s\S]*?<\/analysis>/gi,
  /<reasoning\b[^>]*>[\s\S]*?<\/reasoning>/gi,
] as const

const REASONING_OPENERS = ['<think', '<analysis', '<reasoning'] as const

const REASONING_MARKERS = [
  '<think>',
  '</think>',
  '<analysis>',
  '</analysis>',
  '<reasoning>',
  '</reasoning>',
] as const

/**
 * @param streaming While a message is still streaming, an unclosed opener means
 * "the closing tag has not arrived yet", so everything from it is hidden. Once the
 * message is final that assumption is wrong: gpt-5.x opens a commentary part with a
 * bare `<think>` whose block ends at the part boundary, so `</think>` never arrives
 * at all — truncating from it discarded the entire message (AB#5767). For a final
 * message we therefore drop the marker and keep the text.
 */
export const stripReasoningText = (text: string, streaming = false): string => {
  if (!text) return text

  let output = text

  for (const pattern of REASONING_BLOCKS) {
    output = output.replace(pattern, '')
  }

  for (const opener of REASONING_OPENERS) {
    const openIndex = output.toLowerCase().lastIndexOf(opener)
    if (openIndex === -1) continue

    const previousChar = openIndex > 0 ? output[openIndex - 1] : ''
    // Do not treat literal mentions like "`<think>`" as hidden reasoning blocks.
    if (previousChar === '`') continue

    const closeAfterOpen = output.slice(openIndex).search(/<\/(think|analysis|reasoning)>/i)
    if (closeAfterOpen === -1) {
      if (streaming) {
        output = output.slice(0, openIndex)
      } else {
        // Final message: strip only the opening tag itself, never the body.
        output = output.replace(/<(think|analysis|reasoning)\b[^>]*>/gi, '')
      }
    }
  }

  for (const marker of REASONING_MARKERS) {
    for (let i = marker.length - 1; i > 1; i -= 1) {
      if (output.endsWith(marker.slice(0, i))) {
        output = output.slice(0, -i)
        break
      }
    }
  }

  return output
}
